/**
 * Store in vuex to manage the state of the application
 *
 * @author   pablo.barco
 * @date     2020-06-29
 *
 * @copyright 2020 Smartmatic Corp. All Rights Reserved.
 *
 * @license
 *
 * Confidential information of Smartmatic Labs. Not for disclosure or
 * distribution prior written consent. This software contains code, techniques
 * and know-how which is confidential and proprietary to Smartmatic.
 *
 * Use of this software is subject to the terms of an end user license
 * agreement.
 */

import Vue from "vue";
import Vuex from "vuex";
import VuexPersistence from "vuex-persist";
import VuexI18n from "vuex-i18n";
import Axios from "axios";

import { languages, languageNames, retrieveLanguages } from "../i18n/main";
import getEnv from "../utils/env";
import router from "../router/router";

Vue.use(Vuex);

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
});

const store = new Vuex.Store({
  state: {
    currentLocale: "en",
    loggedUser: null,
    accessToken: null,
    showHints: true,
    mapData: {},
    myLocation: null,
    isTutorialVideoEnabled:
      getEnv("VUE_APP_INTRODUCTION_VIDEO_ENABLED")?.toLowerCase() === "true",
    showVideoTutorial: true,
    siteDetailsDirection: null,
    // this is to set SurveyQuestionScreen in mode read-only
    isCompleteSurvey: false,
    isLoading: false,
    loadedLanguage: false,
    isSurveyViewMode: false,
    hideHeaderBar: false, // If user has logged in from external app, hide the bar
    siteLocations: [],
    resetSurveyButtonHiddenForRoles: [],
    isLoginExternal: false,
    fcmToken: {
      status: "none",
      token: null,
    },
    pushNotifications: [],
  },
  mutations: {
    changeLocale: function (state, { locale }) {
      state.currentLocale = locale;
    },
    addLoggedUser: function (state, { user }) {
      state.loggedUser = user;
    },
    removeLoggedUser: function (state) {
      state.loggedUser = null;
    },
    clearSessionData: function (state) {
      state.loggedUser = null;
      state.accessToken = null;
    },
    addAccessToken: function (state, { token }) {
      state.accessToken = token;
    },
    removeaccessToken: function (state) {
      state.accessToken = null;
    },
    addMapData: function (state, { key, mapData }) {
      // this.$set(state.mapData, key, mapData)
      state.mapData[key] = mapData;
    },
    mapLoaded: function (state, { key }) {
      state.mapData[key].loadingMap = false;
    },
    changeMapCoordinates: function (state, { key, coordinates }) {
      state.mapData[key].coordinates = coordinates;
    },
    updateMyLocation: function (state, coordinates) {
      state.myLocation =
        coordinates?.lat && coordinates?.lng ? coordinates : null;
    },
    dontShowVideo: function (state, { permanentHide }) {
      state.showVideoTutorial = permanentHide;
    },
    setSiteDetailsDirection: function (state, { direction }) {
      state.siteDetailsDirection = direction;
    },
    setIsCompleteSurvey: function (state, { mode }) {
      state.isCompleteSurvey = mode;
    },
    // region Loading State Functions
    showLoading: function (state) {
      state.isLoading = true;
    },
    hideLoading: function (state) {
      state.isLoading = false;
    },
    toggleLoading: function (state) {
      state.isLoading = !state.isLoading;
    },
    setSurveyViewMode: function (state, { mode }) {
      state.isSurveyViewMode = mode;
    },
    setHideHeaderBar: function (state, { hide }) {
      state.hideHeaderBar = hide;
    },
    // endregion
    setSiteLocations: function (state, { siteLocations }) {
      for (const location of siteLocations) {
        const index = state.siteLocations.findIndex((e) => {
          return e.siteId === location.siteId;
        });
        if (index > -1) {
          Vue.set(state.siteLocations, index, location);
        } else {
          state.siteLocations.push(location);
        }
      }
    },
    setResetSurveyButtonHiddenForRoles: function (state, { newValue }) {
      state.resetSurveyButtonHiddenForRoles = newValue;
    },
    setIsLoginExternal: function (state, { isLoginExternal }) {
      state.isLoginExternal = isLoginExternal;
    },
    updateFcmToken: function (state, { value }) {
      state.fcmToken = value;
    },
    setPushNotifications: function (state, { newValue }) {
      state.pushNotifications = newValue;
    },
  },
  actions: {
    changeLocale({ commit }, { locale }) {
      commit("changeLocale", { locale: locale });
    },
    sessionTimeout({ commit }, { data }) {
      if (!data.success && [2, 3, 4, 5].includes(data.code)) {
        commit("clearSessionData");

        if (this.getters.isKeycloakActive()) {
          Vue.prototype.$keycloak.logoutFn();
        } else {
          router.replace({ name: "Login", query: { expired: true } });
        }
      }
    },
    async loginUser(
      { commit },
      { username, password, recaptcha_response, fcmToken }
    ) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}login/login`,
        {
          app: "datacollection",
          recaptcha_response: recaptcha_response,
          fcmToken: fcmToken,
        },
        {
          headers: {
            username: username,
            password: password,
          },
        }
      );
      if (response.data.success) {
        commit("addLoggedUser", { user: response.data.userData });
        commit("addAccessToken", { token: response.data.accesstoken });
        commit("setHideHeaderBar", { hide: false });
      }
      return response.data;
    },
    async loginExternal({ commit }, { token }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}login/loginToken`,
        {},
        {
          headers: { accesstoken: token },
        }
      );

      if (response.data.success) {
        commit("addLoggedUser", { user: response.data.userData });
        commit("addAccessToken", { token: response.data.accesstoken });
        commit("setHideHeaderBar", { hide: true });

        this.dispatch("sessionTimeout", { data: response.data });
      }
      return response.data;
    },
    // Ejemplo de llamada a api con el nuevo sistema de tokens de acceso
    async getAllRoles({ state }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}rol/getAllRolesWithPermissions`,
        {},
        {
          headers: { accesstoken: state.accessToken },
        }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    // Welcome Component
    async getWelcome({ state }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}dataCollection/getWelcome`,
        {},
        {
          headers: { accesstoken: state.accessToken },
        }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    // SiteDetailsScreen
    async getSiteDetails({ state }, { assignmentId }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}dataCollection/getSiteInfo`,
        { assignmentId: assignmentId },
        {
          headers: { accesstoken: state.accessToken },
        }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async getRooms({ state }, { assignmentId }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}dataCollection/getRooms`,
        { assignmentId: assignmentId },
        {
          headers: { accesstoken: state.accessToken },
        }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    // Questions Section
    async getQuestions({ state }, { roomId, assignmentId, roomAssignmentId }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}dataCollection/getQuestions`,
        { roomId, assignmentId, roomAssignmentId },
        { headers: { accesstoken: state.accessToken } }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async requestMyLocation({ commit }) {
      return new Promise((resolve, reject) => {
        if (getEnv("VUE_APP_LOCATION_DISABLED") !== "true") {
          if (!("geolocation" in navigator)) {
            commit("updateMyLocation", null);
            reject("Couldn't get current position");
          } else {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                commit("updateMyLocation", {
                  lat: pos.coords.latitude,
                  lng: pos.coords.longitude,
                });
                resolve(true);
              },
              (err) => {
                commit("updateMyLocation", null);
                reject(err);
              },
              {
                enableHighAccuracy: true,
                maximumAge: 0,
              }
            );
          }
        } else {
          reject("Location disabled by config");
        }
      });
    },
    async saveRoomAnswers(
      { state },
      {
        assignmentId,
        roomId,
        answeredQuestionArray,
        currentLocation,
        justSave,
        origin,
      }
    ) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}dataCollection/saveRoomAnswers`,
        {
          assignmentId: assignmentId,
          roomId: roomId,
          questions: answeredQuestionArray,
          currentLocation: currentLocation,
          justSave: justSave,
          origin: origin,
        },
        { headers: { accesstoken: state.accessToken } }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async addRoom(
      { state },
      {
        roomName,
        roomDescription,
        assignmentId,
        isRecurrentRoom,
        surveyTemplates,
      }
    ) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}dataCollection/addRoom`,
        {
          assignmentId: assignmentId,
          roomName: roomName,
          roomDescription: roomDescription,
          isRecurrentRoom,
          surveyTemplates,
        },
        { headers: { accesstoken: state.accessToken } }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async getTransitionOptions({ state }, { surveyId, assignmentId }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}workflow/getTransitionOptions`,
        { survey: surveyId, assignmentId: assignmentId },
        { headers: { accesstoken: state.accessToken } }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async transitionPhase(
      { state },
      { siteId, roomId, assignmentId, surveyId, transitionId, comment }
    ) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}workflow/transitionPhase`,
        {
          site: siteId,
          roomId: roomId,
          assignmentId: assignmentId,
          surveyId: surveyId,
          transition: transitionId,
          comment: comment,
        },
        { headers: { accesstoken: state.accessToken } }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async drillDownData({ state }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}dataCollection/drillDownData`,
        {},
        { headers: { accesstoken: state.accessToken } }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async saveAssignment({ state }, { site }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}dataCollection/saveAssignment`,
        { site: site },
        { headers: { accesstoken: state.accessToken } }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async getItemsYouNeed({ state }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}item/getItemsYouNeed`,
        {},
        { headers: { accesstoken: state.accessToken } }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async recoveryPasswordForUser({ state }, { username }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}login/passwordRecovery`,
        {
          username: username,
          language: state.currentLocale,
          appCode: "DATACOL",
        },
        { headers: { accesstoken: state.accessToken } }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async getLoginSettings() {
      const response = await Axios.get(
        `${getEnv("VUE_APP_API_URL")}loginSettings/getLoginSettings`
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async resetRoomSurvey({ state }, { roomAssignmentId }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}dataCollection/resetRoomSurvey`,
        { roomAssignmentId: roomAssignmentId },
        { headers: { accesstoken: state.accessToken } }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async getSurveysDataBySite({ state }, { siteId }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}dataCollection/getSurveysDataBySite`,
        {
          siteId: siteId,
        },
        { headers: { accesstoken: state.accessToken } }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async getSingleAssignmentByUser({ state }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}dataCollection/getSingleAssignmentByUser`,
        {},
        { headers: { accesstoken: state.accessToken } }
      );
      this.dispatch("sessionTimeout", { data: response.data });
      return response.data;
    },
    async loginFromKeycloak({ commit }, { keycloak, fcmToken }) {
      const response = await Axios.post(
        `${getEnv("VUE_APP_API_URL")}login/loginWithKeycloakAccessToken`,
        {
          keycloak: keycloak,
          fcmToken: fcmToken,
        }
      );

      if (response.data.success) {
        // We need to rename the property roles to rolesDescriptions
        // let userData = { ...response.data.userData, rolesDescriptions: response.data.userData.roles }

        commit("addLoggedUser", { user: response.data.userData });
        commit("addAccessToken", { token: response.data.accesstoken });
        commit("setHideHeaderBar", { hide: false });
      }

      return response.data;
    },
    async logoffUserFromKeycloak({ commit, state }) {
      await Axios.post(
        `${getEnv("VUE_APP_API_URL")}login/logoff`,
        { app: "client" },
        {
          headers: { accesstoken: state.accessToken },
        }
      );

      await commit("clearSessionData");

      return null;
    },
    async updateAccessToken({ commit, state }, { username, token }) {
      const newToken = `${username}:${token}`;

      await Axios.post(
        `${getEnv("VUE_APP_API_URL")}login/updateAccessToken`,
        {
          newToken: newToken,
        },
        { headers: { accesstoken: state.accessToken } }
      );

      commit("addAccessToken", { token: newToken });
    },
    addPushNotification({ commit, state }, { newNotification }) {
      commit("setPushNotifications", {
        newValue: [...state.pushNotifications, newNotification],
      });
    },
    removePushNotification({ commit, state }) {
      commit("setPushNotifications", {
        newValue: state.pushNotifications.slice(0, -1),
      });
    },
    clearPushNotifications({ commit }) {
      commit("setPushNotifications", { newValue: [] });
    },
  },
  getters: {
    UserHasPermission: (state) => (permission) => {
      let correct = false;
      state.loggedUser.permissions.forEach((p) => {
        if (p.permission === permission) {
          correct = true;
        }
      });
      return correct;
    },
    HasAdminPermission: (state) => (permissionsList) => {
      let haveAccessToAdmin = false;

      const adminPermission = [
        "Roles Management",
        "Users Management",
        "Regions Management",
        "Templates Management",
        "Create Project",
        "Activate/Deactivate Project",
        "Sites Management",
        "Surveys Management",
        "Link surveys, regions and surveyors",
        "Monitoring",
        "Batch Management",
        "System Administration",
        "Delete Transactional Data",
        "Restart Surveys",
        "Manage items you'll need",
        "Reassignment Management",
      ];

      let permissionsListToIterate =
        permissionsList != null
          ? permissionsList
          : state.loggedUser?.permissions ?? [];

      for (let i = 0; i < permissionsListToIterate.length; i++) {
        if (adminPermission.includes(permissionsListToIterate[i].permission)) {
          haveAccessToAdmin = true;
          break;
        }
      }

      return haveAccessToAdmin;
    },
    HasDataColPermission: (state) => (permissionsList) => {
      let haveAccessToDataCol = false;

      const dataColPermission = [
        "List of to do - surveyor",
        "Practice mode",
        "Drill Down Surveyor",
        "Add Room",
      ];

      let permissionsListToIterate =
        permissionsList != null
          ? permissionsList
          : state.loggedUser.permissions;

      for (let i = 0; i < permissionsListToIterate.length; i++) {
        if (
          dataColPermission.includes(permissionsListToIterate[i].permission)
        ) {
          haveAccessToDataCol = true;
          break;
        }
      }

      return haveAccessToDataCol;
    },
    SystemUlrBase: () => () => {
      let fullUrl = window.location.href;

      let splitted = fullUrl.split("/");

      let firstPart = splitted[0] + "//" + splitted[2];

      return firstPart;
    },
    HideHeaderBar: (state) => () => {
      return state.hideHeaderBar;
    },
    GetUserRoles: (state) => () => {
      return state.loggedUser.roles;
    },
    GetUserData: (state) => () => {
      return state.loggedUser;
    },
    isLoginExternal: (state) => () => {
      return state.isLoginExternal;
    },
    isKeycloakActive: () => () => {
      return (
        getEnv("VUE_APP_KEYCLOAK_ACTIVE")?.toString()?.toLowerCase() === "true"
      );
    },
    GetLastPushNotification: (state) => {
      return (
        state.pushNotifications[state.pushNotifications.length - 1] ?? null
      );
    },
    GetPushNotificationCount: (state) => {
      return state.pushNotifications.length;
    },
  },
  plugins: [vuexLocal.plugin],
});

Vue.use(VuexI18n.plugin, store);
store.state.loadedLanguage = false;
retrieveLanguages(loadI18n);
function loadI18n() {
  for (let i = 0; i < languages.length; i++) {
    Vue.i18n.add(languages[i].language, languages[i].messages);
  }
  // We set the language based on the browser's language preference
  const navigatorLang = navigator.language.split("-")[0];
  let lang = languageNames.find((l) => l.key === navigatorLang)?.key;

  if (!lang) {
    console.warn(
      `Language ${navigatorLang} is not available. Falling back to English.`
    );
    lang = "en";
  }

  store.commit("changeLocale", { locale: lang });
  Vue.i18n.set(store.state.currentLocale);
  store.state.loadedLanguage = true;
}

export default store;
